import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { AppModelModalComponent } from '@nimmsta/ng-commons-lib/forms';
import {
  Column,
  CustomAction,
  DataTableComponent,
  EmptyTableComponent,
} from '@nimmsta/ng-commons-lib/data-table';
import { HeaderBarComponent } from '@nimmsta/ng-commons-lib/header-bar';
import { SoftwareArtifactReleaseType } from '@nimmsta/ng-commons-lib/models';
import { SoftwareArtifactReleaseTypeFormComponent } from './software-artifact-release-type-form.component';
import { SoftwareLabelDatasource } from './software-label.datasource';
import { SoftwareArtifactReleaseTypeService } from '../../services/software/software-artifact-release-type.service';

@Component({
  selector: 'app-software-artifact-release-type',
  templateUrl: './software-artifact-release-type.component.html',
  imports: [
    SoftwareArtifactReleaseTypeFormComponent,
    TranslatePipe,
    HeaderBarComponent,
    DataTableComponent,
    EmptyTableComponent,
    AppModelModalComponent,
  ],
})
export class SoftwareArtifactReleaseTypeComponent implements OnInit {
  private softwareArtifactReleaseTypeService = inject(
    SoftwareArtifactReleaseTypeService,
  );
  private translate = inject(TranslateService);
  dataSource!: SoftwareLabelDatasource<SoftwareArtifactReleaseType>;

  @ViewChild(DataTableComponent, { static: true })
  private tableComponent!: DataTableComponent<SoftwareArtifactReleaseType>;

  @ViewChild('EditModal', { static: true })
  private editModalComponent!: AppModelModalComponent<SoftwareArtifactReleaseType>;

  @ViewChild(SoftwareArtifactReleaseTypeFormComponent, { static: true })
  private formComponent!: SoftwareArtifactReleaseTypeFormComponent;

  customActions: CustomAction<SoftwareArtifactReleaseType>[] = [];

  get columns(): Column[] {
    return [
      new Column('name', 'software.label.name', { sortable: true }),
      new Column('description', 'software.label.description'),
    ];
  }

  ngOnInit(): void {
    this.dataSource = new SoftwareLabelDatasource<SoftwareArtifactReleaseType>(
      this.softwareArtifactReleaseTypeService,
    );
    const editAction = new CustomAction('edit');
    this.customActions.push(editAction);
    editAction.subscribe((id) => this.edit(id));
    const deleteAction = new CustomAction('delete', false, true);
    this.customActions.push(deleteAction);
    deleteAction.subscribe((id) => this.delete(id));
  }

  new(): void {
    this.translate.get('new').subscribe((value) => {
      this.openModal(value, true, new SoftwareArtifactReleaseType());
    });
  }

  show(id: string): void {
    this.translate.get('details').subscribe((value) => {
      this.openModal(value, false, this.getSelectedObject(id)!);
    });
  }

  edit(id: string): void {
    this.translate.get('edit').subscribe((value) => {
      this.openModal(value, true, this.getSelectedObject(id)!);
    });
  }

  openModal(
    title: string,
    editable: boolean,
    type: SoftwareArtifactReleaseType,
  ): void {
    this.formComponent.editable = editable;
    this.formComponent.setValue(type);
    this.editModalComponent.title = title;
    this.editModalComponent.editable = editable;
    this.editModalComponent.show();
  }

  delete(id: string): void {
    lastValueFrom(
      this.softwareArtifactReleaseTypeService.delete(
        this.getSelectedObject(id)!,
      ),
    ).then(() => this.tableComponent.loadDataPage());
  }

  onSave(): void {
    this.formComponent.onSave();
  }

  onModalClose(): void {
    this.editModalComponent.hide();
    this.tableComponent.loadDataPage();
  }

  getSelectedObject(id: string): SoftwareArtifactReleaseType | undefined {
    return this.dataSource.data?.content.filter((d) => id === d.id).pop();
  }

  onLoading(loading: boolean): void {
    this.editModalComponent.loading = loading;
  }
}
